import { getIndexedDb, IndexedDBType } from "utils/indexedDb";
import { ChangedResponseData, ErrorResponse } from "./BaseRequestServiceWorker";
import { API_GET_CACHE, CACHE_QUERY_PARAM } from "constants/serviceWorker";
import { addToCache, matchToCache } from "./cache";

const SYNC_OFFLINE_PARAMS = "syncOffline";

export type BaseCustomResponseParams = {
  event: FetchEvent;
  swOnlineStatus: boolean;
  storeNameOfRequest: string;
  params: Record<string, any>;
};

export default abstract class BaseCustomResponse {
  protected event: FetchEvent;
  protected swOnlineStatus: boolean;
  protected storeNameOfRequest: string = "";
  protected params: Record<string, any> = {};
  protected indexedDb: IndexedDBType | undefined = undefined;

  constructor(args: BaseCustomResponseParams) {
    const { event, swOnlineStatus, storeNameOfRequest, params } = args;
    this.event = event;
    this.swOnlineStatus = swOnlineStatus;
    this.storeNameOfRequest = storeNameOfRequest;
    this.params = params;
  }
  async getIndexedDb() {
    if (this.indexedDb) {
      return this.indexedDb;
    }

    this.indexedDb = await getIndexedDb();

    return this.indexedDb;
  }

  isRequestSyncData(url: string) {
    return (
      new URLSearchParams(new URL(url).search).get(SYNC_OFFLINE_PARAMS) ===
      "true"
    );
  }

  isCacheRequest() {
    const urlObj = new URL(this.event.request.url);

    return urlObj.searchParams.get(CACHE_QUERY_PARAM) === "true";
  }

  async getRequestForFetch() {
    if (this.isCacheRequest()) {
      const req = this.event.request;
      const urlObj = new URL(req.url);
      urlObj.searchParams.delete(CACHE_QUERY_PARAM);
      // return request;
      const body = req.body ? await req.clone().text() : null;
      const clonedReq = new Request(urlObj.href, {
        method: req.method,
        headers: new Headers(req.headers),
        body: body,
        mode: req.mode,
        credentials: req.credentials,
        cache: req.cache,
        redirect: req.redirect,
        referrer: req.referrer,
        integrity: req.integrity,
        keepalive: req.keepalive,
        signal: req.signal,
      });

      return clonedReq;
    }

    return this.event.request.clone();
  }

  async response(params: ChangedResponseData) {
    const { response, data, isPutToCache = true } = params;
    const request = this.event.request.clone();
    const newResponse = new Response(JSON.stringify(data), {
      headers: response?.headers,
      status: response?.status || 202,
      statusText: response?.statusText,
    });
    const isCache =
      (isPutToCache && this.swOnlineStatus) ||
      (this.isCacheRequest() && request.method === "GET");

    if (newResponse.ok && isCache) {
      await addToCache(API_GET_CACHE, request, newResponse);
    }

    return newResponse;
  }

  abstract changeResponseData(): Promise<Response>;

  async responseByCacheFirst(cacheName: string) {
    const event = this.event;
    const cache = await caches.open(cacheName);
    try {
      const cachedResource = await matchToCache(
        cacheName,
        event.request,
        cache
      );
      if (cachedResource) {
        return cachedResource;
      }
      if (!this.swOnlineStatus) {
        return ErrorResponse.clone();
      }
      const req = await this.getRequestForFetch();
      const response = await fetch(req);
      if (response.ok) {
        await addToCache(cacheName, event.request, response, cache);
      }

      return response;
    } catch (err) {
      console.log(err);

      return ErrorResponse.clone();
    }
  }
}
